import React from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Download, Upload, Archive, ArchiveRestore, Trash2, AlertTriangle, CheckSquare, Square, ChevronDown } from "lucide-react";

const TABS = [
    { key: "price", label: "Ajustare prețuri" },
    { key: "stock", label: "Import stoc CSV" },
    { key: "archive", label: "Arhivare produse" },
];

// ── Price adjustment tab ─────────────────────────────────────────────────────
function PriceTab() {
    const [scopes, setScopes] = React.useState({ categories: [], brands: [] });
    const [form, setForm] = React.useState({
        scope: "all",
        scope_value: "",
        field: "price",
        operation: "increase_pct",
        value: "",
        round_to: 2,
    });
    const [preview, setPreview] = React.useState(null);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        api.get("/admin/products/bulk/scopes").then(r => setScopes(r.data)).catch(() => {});
    }, []);

    const loadPreview = async () => {
        try {
            const r = await api.get("/admin/products/bulk/price-preview", {
                params: { scope: form.scope, scope_value: form.scope_value }
            });
            setPreview(r.data);
        } catch { toast.error("Eroare la previzualizare"); }
    };

    const apply = async () => {
        if (!form.value || Number(form.value) <= 0) return toast.error("Introdu o valoare pozitivă");
        if (!window.confirm(`Aplici ajustarea pe ${preview?.total ?? "?"} produse?`)) return;
        setLoading(true);
        try {
            const r = await api.post("/admin/products/bulk/price-adjust", {
                ...form, value: Number(form.value)
            });
            toast.success(`${r.data.updated} produse actualizate`);
            setPreview(null);
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
        setLoading(false);
    };

    const OPERATION_LABELS = {
        increase_pct: "Mărește cu %",
        decrease_pct: "Scade cu %",
        set_fixed:    "Setează fix (RON)",
    };

    const FIELD_LABELS = {
        price:      "Preț vânzare",
        sale_price: "Preț promoțional",
        both:       "Ambele prețuri",
    };

    return (
        <div className="space-y-5 max-w-2xl">
            <p className="text-sm text-muted-foreground">
                Modifică prețurile în masă pentru o categorie, un brand sau toate produsele.
            </p>

            <div className="card-soft p-5 space-y-4">
                {/* Scope */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="label-soft">Domeniu</label>
                        <select value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value, scope_value: "" }))} className="input-soft">
                            <option value="all">Toate produsele</option>
                            <option value="category">Categorie</option>
                            <option value="brand">Brand</option>
                        </select>
                    </div>
                    {form.scope !== "all" && (
                        <div>
                            <label className="label-soft">{form.scope === "category" ? "Categorie" : "Brand"}</label>
                            <select value={form.scope_value} onChange={e => setForm(f => ({ ...f, scope_value: e.target.value }))} className="input-soft">
                                <option value="">— selectează —</option>
                                {form.scope === "category"
                                    ? scopes.categories.map(c => <option key={c.category_id} value={c.slug}>{c.name}</option>)
                                    : scopes.brands.map(b => <option key={b} value={b}>{b}</option>)
                                }
                            </select>
                        </div>
                    )}
                </div>

                {/* Field & operation */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="label-soft">Câmp preț</label>
                        <select value={form.field} onChange={e => setForm(f => ({ ...f, field: e.target.value }))} className="input-soft">
                            {Object.entries(FIELD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="label-soft">Operațiune</label>
                        <select value={form.operation} onChange={e => setForm(f => ({ ...f, operation: e.target.value }))} className="input-soft">
                            {Object.entries(OPERATION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </div>
                </div>

                {/* Value */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="label-soft">
                            {form.operation === "set_fixed" ? "Valoare (RON)" : "Procent (%)"}
                        </label>
                        <input
                            type="number" step="0.01" min="0"
                            value={form.value}
                            onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
                            placeholder={form.operation === "set_fixed" ? "199.99" : "5"}
                            className="input-soft"
                        />
                    </div>
                    <div>
                        <label className="label-soft">Rotunjire (zecimale)</label>
                        <select value={form.round_to} onChange={e => setForm(f => ({ ...f, round_to: Number(e.target.value) }))} className="input-soft">
                            <option value={0}>0 (întreg)</option>
                            <option value={2}>2 (ex: 19.99)</option>
                        </select>
                    </div>
                </div>

                {/* Preview + Apply */}
                <div className="flex items-center gap-3 pt-1">
                    <button
                        type="button" onClick={loadPreview}
                        disabled={form.scope !== "all" && !form.scope_value}
                        className="btn-outline flex items-center gap-2"
                    >
                        Previzualizare
                    </button>
                    <button
                        type="button" onClick={apply}
                        disabled={loading || !form.value || (form.scope !== "all" && !form.scope_value)}
                        className="btn-primary flex items-center gap-2"
                    >
                        {loading ? "Se aplică..." : "Aplică"}
                    </button>
                </div>
            </div>

            {preview && (
                <div className="card-soft p-4">
                    <div className="text-sm font-medium mb-3">
                        <span className="text-primary text-lg font-heading font-bold">{preview.total}</span> produse vor fi afectate
                    </div>
                    {preview.sample.length > 0 && (
                        <table className="w-full text-sm">
                            <thead><tr className="text-xs text-muted-foreground border-b border-border">
                                <th className="pb-2 text-left">Produs</th>
                                <th className="pb-2 text-right">Preț actual</th>
                                <th className="pb-2 text-right">Preț promoțional</th>
                            </tr></thead>
                            <tbody>{preview.sample.map((p, i) => (
                                <tr key={i} className="border-b border-border/40 last:border-0">
                                    <td className="py-1.5">
                                        <div className="font-medium truncate max-w-[220px]">{p.name}</div>
                                        <div className="text-xs text-muted-foreground font-mono">{p.code}</div>
                                    </td>
                                    <td className="py-1.5 text-right font-mono">{p.price ? `${Number(p.price).toFixed(2)} RON` : "—"}</td>
                                    <td className="py-1.5 text-right font-mono text-primary">{p.sale_price ? `${Number(p.sale_price).toFixed(2)} RON` : "—"}</td>
                                </tr>
                            ))}</tbody>
                        </table>
                    )}
                    {preview.total > 10 && <p className="text-xs text-muted-foreground mt-2">... și încă {preview.total - 10} produse</p>}
                </div>
            )}
        </div>
    );
}

// ── Stock import tab ─────────────────────────────────────────────────────────
function StockTab() {
    const [file, setFile] = React.useState(null);
    const [result, setResult] = React.useState(null);
    const [loading, setLoading] = React.useState(false);

    const downloadTemplate = async () => {
        const r = await api.get("/admin/products/bulk/stock-template", { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
        const a = document.createElement("a"); a.href = url; a.download = "template-stoc.csv";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
    };

    const doImport = async () => {
        if (!file) return toast.error("Selectează un fișier CSV");
        setLoading(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            const r = await api.post("/admin/products/bulk/stock-import", fd);
            setResult(r.data);
            toast.success(`${r.data.updated} produse actualizate`);
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare la import"); }
        setLoading(false);
    };

    return (
        <div className="space-y-5 max-w-2xl">
            <p className="text-sm text-muted-foreground">
                Importă stocul din CSV. Coloane necesare: <code className="bg-muted px-1 rounded text-xs">code</code>, <code className="bg-muted px-1 rounded text-xs">stock</code>. Opțional: <code className="bg-muted px-1 rounded text-xs">stock_supplier</code>.
            </p>

            <div className="card-soft p-5 space-y-4">
                <button onClick={downloadTemplate} type="button" className="btn-outline flex items-center gap-2 text-sm">
                    <Download className="h-4 w-4" /> Descarcă template CSV
                </button>

                <div>
                    <label className="label-soft block mb-1.5">Fișier CSV</label>
                    <div className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${file ? "border-primary/50 bg-primary/5" : "border-border hover:border-primary/30"}`}>
                        <input type="file" accept=".csv,text/csv" className="hidden" id="stock-csv-input"
                            onChange={e => { setFile(e.target.files[0]); setResult(null); }} />
                        <label htmlFor="stock-csv-input" className="cursor-pointer">
                            <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                            {file
                                ? <div className="font-medium text-sm">{file.name} <span className="text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span></div>
                                : <div className="text-sm text-muted-foreground">Click sau drag & drop</div>
                            }
                        </label>
                    </div>
                </div>

                <button onClick={doImport} disabled={!file || loading} className="btn-primary flex items-center gap-2">
                    <Upload className="h-4 w-4" /> {loading ? "Se importă..." : "Importă stoc"}
                </button>
            </div>

            {result && (
                <div className="card-soft p-5 space-y-3">
                    <h4 className="font-heading text-base">Rezultat import</h4>
                    <div className="grid grid-cols-3 gap-3 text-center">
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                            <div className="text-2xl font-heading font-bold text-emerald-600">{result.updated}</div>
                            <div className="text-xs text-muted-foreground">Actualizate</div>
                        </div>
                        <div className={`border rounded-xl p-3 ${result.not_found > 0 ? "bg-amber-50 border-amber-200" : "bg-muted/30 border-border"}`}>
                            <div className={`text-2xl font-heading font-bold ${result.not_found > 0 ? "text-amber-600" : "text-muted-foreground"}`}>{result.not_found}</div>
                            <div className="text-xs text-muted-foreground">Negăsite</div>
                        </div>
                        <div className={`border rounded-xl p-3 ${result.errors > 0 ? "bg-destructive/5 border-destructive/30" : "bg-muted/30 border-border"}`}>
                            <div className={`text-2xl font-heading font-bold ${result.errors > 0 ? "text-destructive" : "text-muted-foreground"}`}>{result.errors}</div>
                            <div className="text-xs text-muted-foreground">Erori</div>
                        </div>
                    </div>

                    {result.not_found_codes?.length > 0 && (
                        <div>
                            <div className="text-xs font-semibold text-amber-600 mb-1">Coduri negăsite:</div>
                            <div className="text-xs text-muted-foreground font-mono bg-muted rounded-lg p-2 max-h-24 overflow-y-auto">
                                {result.not_found_codes.join(", ")}
                            </div>
                        </div>
                    )}
                    {result.error_details?.length > 0 && (
                        <div>
                            <div className="text-xs font-semibold text-destructive mb-1">Erori:</div>
                            <div className="space-y-0.5 max-h-32 overflow-y-auto">
                                {result.error_details.map((e, i) => (
                                    <div key={i} className="text-xs text-destructive bg-destructive/5 rounded px-2 py-1">
                                        Rând {e.row} {e.code ? `(${e.code})` : ""}: {e.error}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Archive tab ──────────────────────────────────────────────────────────────
function ArchiveTab() {
    const [filters, setFilters] = React.useState({ no_stock: true, no_sales_days: 365, scope: "all", scope_value: "" });
    const [scopes, setScopes] = React.useState({ categories: [], brands: [] });
    const [candidates, setCandidates] = React.useState(null);
    const [selected, setSelected] = React.useState(new Set());
    const [loadingCandidates, setLoadingCandidates] = React.useState(false);
    const [action, setAction] = React.useState("archive"); // archive | restore | delete
    const [applying, setApplying] = React.useState(false);

    React.useEffect(() => {
        api.get("/admin/products/bulk/scopes").then(r => setScopes(r.data)).catch(() => {});
    }, []);

    const loadCandidates = async () => {
        setLoadingCandidates(true);
        setCandidates(null); setSelected(new Set());
        try {
            const r = await api.get("/admin/products/bulk/archive-candidates", { params: filters });
            setCandidates(r.data);
        } catch { toast.error("Eroare la încărcare"); }
        setLoadingCandidates(false);
    };

    const toggleAll = () => {
        if (!candidates) return;
        if (selected.size === candidates.products.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(candidates.products.map(p => p.product_id)));
        }
    };

    const toggle = (id) => {
        const s = new Set(selected);
        if (s.has(id)) s.delete(id); else s.add(id);
        setSelected(s);
    };

    const applyAction = async () => {
        if (selected.size === 0) return toast.error("Selectează cel puțin un produs");
        const ids = [...selected];
        const labels = { archive: "arhivezi", restore: "restaurezi", delete: "ștergi definitiv" };
        if (!window.confirm(`${labels[action] || action} ${ids.length} produse?`)) return;
        setApplying(true);
        try {
            let r;
            if (action === "archive") {
                r = await api.post("/admin/products/bulk/archive", { product_ids: ids, confirm: true });
                toast.success(`${r.data.archived} produse arhivate`);
            } else if (action === "restore") {
                r = await api.post("/admin/products/bulk/restore", { product_ids: ids });
                toast.success(`${r.data.restored} produse restaurate`);
            } else if (action === "delete") {
                r = await api.delete("/admin/products/bulk/delete", { data: { product_ids: ids, confirm: true } });
                toast.success(`${r.data.deleted} produse șterse definitiv`);
            }
            setCandidates(null); setSelected(new Set());
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
        setApplying(false);
    };

    const allSelected = candidates && selected.size === candidates.products.length && candidates.products.length > 0;

    return (
        <div className="space-y-5 max-w-3xl">
            <p className="text-sm text-muted-foreground">
                Identifică și arhivează produse vechi, fără stoc sau fără vânzări recente.
            </p>

            <div className="card-soft p-5 space-y-4">
                <h4 className="font-heading text-base">Filtre</h4>
                <div className="grid grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={filters.no_stock} onChange={e => setFilters(f => ({ ...f, no_stock: e.target.checked }))} className="accent-primary" />
                        Stoc fizic = 0
                    </label>
                    <div className="flex items-center gap-2">
                        <label className="text-sm whitespace-nowrap">Fără vânzări de</label>
                        <input type="number" min="0" value={filters.no_sales_days}
                            onChange={e => setFilters(f => ({ ...f, no_sales_days: Number(e.target.value) }))}
                            className="input-soft !py-1 w-20 text-sm" />
                        <span className="text-sm text-muted-foreground">zile</span>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="label-soft">Domeniu</label>
                        <select value={filters.scope} onChange={e => setFilters(f => ({ ...f, scope: e.target.value, scope_value: "" }))} className="input-soft">
                            <option value="all">Toate produsele</option>
                            <option value="category">Categorie</option>
                            <option value="brand">Brand</option>
                        </select>
                    </div>
                    {filters.scope !== "all" && (
                        <div>
                            <label className="label-soft">{filters.scope === "category" ? "Categorie" : "Brand"}</label>
                            <select value={filters.scope_value} onChange={e => setFilters(f => ({ ...f, scope_value: e.target.value }))} className="input-soft">
                                <option value="">— selectează —</option>
                                {filters.scope === "category"
                                    ? scopes.categories.map(c => <option key={c.category_id} value={c.slug}>{c.name}</option>)
                                    : scopes.brands.map(b => <option key={b} value={b}>{b}</option>)
                                }
                            </select>
                        </div>
                    )}
                </div>

                <button onClick={loadCandidates} disabled={loadingCandidates} className="btn-outline">
                    {loadingCandidates ? "Se caută..." : "Caută produse candidate"}
                </button>
            </div>

            {candidates && (
                <div className="card-soft overflow-hidden">
                    <div className="p-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-sm">
                            <span className="font-heading text-lg font-bold">{candidates.total}</span> produse găsite
                            {candidates.total > 200 && <span className="text-muted-foreground ml-1">(afișate primele 200)</span>}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <select value={action} onChange={e => setAction(e.target.value)} className="input-soft !py-1.5 text-sm">
                                <option value="archive">Arhivează (ascunde din catalog)</option>
                                <option value="restore">Restaurează</option>
                                <option value="delete">Șterge definitiv</option>
                            </select>
                            <button
                                onClick={applyAction}
                                disabled={selected.size === 0 || applying}
                                className={`btn-primary text-sm flex items-center gap-1.5 ${action === "delete" ? "!bg-destructive hover:!bg-destructive/90" : ""}`}
                            >
                                {action === "archive" && <Archive className="h-3.5 w-3.5" />}
                                {action === "restore" && <ArchiveRestore className="h-3.5 w-3.5" />}
                                {action === "delete" && <Trash2 className="h-3.5 w-3.5" />}
                                {applying ? "Se aplică..." : `Aplică pe ${selected.size} selectate`}
                            </button>
                        </div>
                    </div>

                    {action === "delete" && (
                        <div className="flex items-start gap-2 px-4 py-2 bg-destructive/5 border-b border-destructive/20 text-xs text-destructive">
                            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            Ștergerea definitivă este ireversibilă. Foloseşte arhivarea în schimb dacă nu ești sigur.
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted">
                                <tr>
                                    <th className="px-3 py-2 text-left w-8">
                                        <button onClick={toggleAll}>
                                            {allSelected ? <CheckSquare className="h-4 w-4 text-primary" /> : <Square className="h-4 w-4 text-muted-foreground" />}
                                        </button>
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs uppercase text-muted-foreground">Produs</th>
                                    <th className="px-3 py-2 text-right text-xs uppercase text-muted-foreground">Stoc</th>
                                    <th className="px-3 py-2 text-right text-xs uppercase text-muted-foreground">Ultima vânzare</th>
                                    <th className="px-3 py-2 text-right text-xs uppercase text-muted-foreground">Creat</th>
                                </tr>
                            </thead>
                            <tbody>
                                {candidates.products.length === 0 && (
                                    <tr><td colSpan="5" className="text-center py-8 text-muted-foreground">Niciun produs găsit cu criteriile selectate.</td></tr>
                                )}
                                {candidates.products.map(p => (
                                    <tr key={p.product_id} className={`border-t border-border ${selected.has(p.product_id) ? "bg-primary/5" : ""}`}>
                                        <td className="px-3 py-2">
                                            <button onClick={() => toggle(p.product_id)}>
                                                {selected.has(p.product_id)
                                                    ? <CheckSquare className="h-4 w-4 text-primary" />
                                                    : <Square className="h-4 w-4 text-muted-foreground" />}
                                            </button>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="font-medium max-w-[200px] truncate">{p.name}</div>
                                            <div className="text-xs font-mono text-muted-foreground">{p.code}</div>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <span className={`font-mono text-sm ${(p.stock ?? 0) <= 0 ? "text-destructive" : "text-muted-foreground"}`}>
                                                {p.stock ?? 0}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right text-xs text-muted-foreground">
                                            {p.last_sold_at ? new Date(p.last_sold_at).toLocaleDateString("ro-RO") : "Niciodată"}
                                        </td>
                                        <td className="px-3 py-2 text-right text-xs text-muted-foreground">
                                            {p.created_at ? new Date(p.created_at).toLocaleDateString("ro-RO") : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminBulkOps() {
    const [tab, setTab] = React.useState("price");

    return (
        <div data-testid="admin-bulk-ops">
            <div className="overline mb-1">Administrare</div>
            <h1 className="font-heading text-3xl mb-2">Operațiuni în masă</h1>
            <p className="text-sm text-muted-foreground mb-6">
                Ajustare prețuri per categorie/brand, import stoc din CSV, arhivare produse vechi.
            </p>

            <div className="flex gap-1 border-b border-border mb-6">
                {TABS.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        className={`px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${tab === t.key ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "price"   && <PriceTab />}
            {tab === "stock"   && <StockTab />}
            {tab === "archive" && <ArchiveTab />}
        </div>
    );
}
